import {Injectable} from '@angular/core';
import {NotificationModel} from '../../core-logic/models/notification/notification-model';
import {NotificationGatewayHttp} from '../../adapters/real/api-services/gateways/notification-gateway-http';
import {BehaviorSubject, Observable, zip} from 'rxjs';
import {isErrorModel} from '../functions/utilities-functions';
import {RequestOptionsModel} from '../../core-logic/models/shared/request-options-model';
import _ from 'lodash';
import {map} from 'rxjs/operators';
import {UserTypeList} from '../enums/user-type-list';

@Injectable({
  providedIn: 'root'
})
export class NotificationManagerService {
  private merchantNotificationsSubject$: BehaviorSubject<NotificationModel []> = new BehaviorSubject<NotificationModel[]>([]);
  private customerNotificationsSubject$: BehaviorSubject<NotificationModel []> = new BehaviorSubject<NotificationModel[]>([]);
  private customerNotificationSliderControllerSubject$: BehaviorSubject<NotificationModel> =
    new BehaviorSubject<NotificationModel>({} as NotificationModel);
  private merchantNotificationSliderControllerSubject$: BehaviorSubject<NotificationModel> =
    new BehaviorSubject<NotificationModel>({} as NotificationModel);
  constructor(
    private notificationGatewayHttp: NotificationGatewayHttp
  ) {}

  init() {
    zip(
      this.notificationGatewayHttp.getBusinessNotificationsByUserType({type: UserTypeList.BUSINESS_OWNER}),
      this.notificationGatewayHttp.getBusinessNotificationsByUserType({type: UserTypeList.CUSTOMER}),
    ).subscribe(([merchantNotifications, customerNotifications]) => {
      this.merchantNotificationsSubject$.next(isErrorModel(merchantNotifications) ? [] : merchantNotifications as NotificationModel []);
      this.customerNotificationsSubject$.next(isErrorModel(customerNotifications) ? [] : customerNotifications as NotificationModel []);
    });
  }

  getMerchantNotifications$(): Observable<NotificationModel []> {
    return this.merchantNotificationsSubject$.asObservable();
  }

  paginateMerchantNotification(requestOptions: RequestOptionsModel): Observable<boolean> {
    return this.notificationGatewayHttp.getBusinessNotificationsByUserType({type: UserTypeList.BUSINESS_OWNER,
      request_option: requestOptions})
      .pipe(map(paginatedNotifications => {
        if (isErrorModel(paginatedNotifications)) { return false; }
        const notifications = this.merchantNotificationsSubject$.getValue();
        notifications.push(... paginatedNotifications as NotificationModel [])
        _.uniqBy(notifications, '_id');
        this.merchantNotificationsSubject$.next(notifications);
        return true;
      }));
  }

  paginateCustomerNotification(requestOptions: RequestOptionsModel): Observable<boolean> {
    return this.notificationGatewayHttp.getBusinessNotificationsByUserType({type: UserTypeList.CUSTOMER,
      request_option: requestOptions})
      .pipe(map(paginatedNotifications => {
        if (isErrorModel(paginatedNotifications)) { return false; }
        const notifications = this.customerNotificationsSubject$.getValue();
        notifications.push(... paginatedNotifications as NotificationModel [])
        _.uniqBy(notifications, '_id');
        this.customerNotificationsSubject$.next(notifications);
        return true;
      }));
  }

  getCustomerNotifications$(): Observable<NotificationModel []> {
    return this.customerNotificationsSubject$.asObservable();
  }

  refreshNotifications(notification: NotificationModel) {
    if (notification.targeted.customer) {
      const notifications =  this.customerNotificationsSubject$.getValue();
      const foundedNotificationIndex = notifications.findIndex(wantedNotification => wantedNotification._id === notification._id);
      foundedNotificationIndex >= 0 ? notifications[foundedNotificationIndex] = notification : notifications.unshift(notification);
      this.customerNotificationsSubject$.next(notifications);
    }

    if (notification.targeted.merchant) {
      const notifications =  this.merchantNotificationsSubject$.getValue();
      const foundedNotificationIndex = notifications.findIndex(wantedNotification => wantedNotification._id === notification._id);
      foundedNotificationIndex >= 0 ? notifications[foundedNotificationIndex] = notification : notifications.unshift(notification);
      this.merchantNotificationsSubject$.next(notifications);
    }
  }

  setCustomerSliderOptionsSubject(notificationModel: NotificationModel): void {
    return this.customerNotificationSliderControllerSubject$.next(notificationModel);
  }

  getCustomerSliderOptionsSubject$(): Observable<NotificationModel> {
    return this.customerNotificationSliderControllerSubject$.asObservable();
  }

  setMerchantSliderOptionsSubject(notificationModel: NotificationModel): void {
    return this.merchantNotificationSliderControllerSubject$.next(notificationModel);
  }

  getMerchantSliderOptionsSubject$(): Observable<NotificationModel> {
    return this.merchantNotificationSliderControllerSubject$.asObservable();
  }
}
